import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { InventoryItem, Slot } from "./warehouse-types";
import { RenameModal } from "./RenameModal";
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui";
import { Select } from "@mantine/core";
import {
  IconPackage,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";

export interface ProductOption {
  id: string;
  name: string;
}

export type NewInventoryItem = Omit<InventoryItem, "id">;

interface SlotDetailPanelProps {
  slot: Slot | null;
  products: ProductOption[];
  onUpdateItem: (
    slotId: string,
    itemId: string,
    updates: Partial<InventoryItem>
  ) => void;
  onAddItem: (slotId: string, item: NewInventoryItem) => void;
  onDeleteItem: (slotId: string, itemId: string) => void;
  onRename: (slotId: string, name: string) => void;
  onClose: () => void;
}

const selectStyles = {
  input: {
    backgroundColor: "#0d0f15",
    borderColor: "#30333c",
    color: "#f4f4f5",
  },
} as const;

const selectClassNames = {
  dropdown: "!border-[#30333c] !bg-[#1a1d24] !shadow-lg",
  option:
    "!text-zinc-100 hover:!bg-[#2a2d35] data-[combobox-selected]:!bg-[#2a2d35] data-[combobox-selected]:!text-teal-300",
  empty: "!text-zinc-400",
} as const;

interface ItemFormModalProps {
  mode: "add" | "edit";
  products: ProductOption[];
  existingItems: InventoryItem[];
  item?: InventoryItem | null;
  onClose: () => void;
  onSubmitAdd: (draft: NewInventoryItem) => void;
  onSubmitEdit: (itemId: string, updates: Partial<InventoryItem>) => void;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-zinc-400">{label}</span>
      {children}
    </label>
  );
}

function ReadOnlyValue({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-9 items-center rounded-md border border-[#30333c] bg-[#2a2d35] px-3 text-sm text-zinc-300">
      {children}
    </div>
  );
}

function ItemFormModal({
  mode,
  products,
  existingItems,
  item,
  onClose,
  onSubmitAdd,
  onSubmitEdit,
}: ItemFormModalProps) {
  const { t } = useTranslation();
  const [productId, setProductId] = useState(item?.productId ?? "");
  const [quantity, setQuantity] = useState(
    item ? String(item.quantity) : ""
  );
  const [limit, setLimit] = useState(item ? String(item.limit) : "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const productData = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const selectedProduct = products.find(
    (p) => p.id === (item?.productId ?? productId)
  );

  const handleSubmit = () => {
    const quantityValue = parseInt(quantity, 10);
    const limitValue = parseInt(limit, 10);

    if (mode === "add") {
      if (!productId) {
        setError(t("warehouseMap.slotDetail.selectProductRequired"));
        return;
      }
      if (existingItems.some((i) => i.productId === productId)) {
        setError(t("warehouseMap.slotDetail.duplicateProduct"));
        return;
      }
    }

    if (!quantity || Number.isNaN(quantityValue) || quantityValue < 0) {
      setError(t("warehouseMap.slotDetail.invalidQuantity"));
      return;
    }
    if (!limit || Number.isNaN(limitValue) || limitValue < 0) {
      setError(t("warehouseMap.slotDetail.invalidLimit"));
      return;
    }

    if (mode === "add") {
      onSubmitAdd({
        productId,
        name: selectedProduct?.name ?? "",
        quantity: quantityValue,
        limit: limitValue,
      });
      return;
    }

    if (item) {
      onSubmitEdit(item.id, { quantity: quantityValue, limit: limitValue });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-[#30333c] bg-[#1a1d24] shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#30333c] px-5 py-4">
          <h3 className="text-base font-semibold text-zinc-100">
            {mode === "add"
              ? t("warehouseMap.slotDetail.addItem")
              : t("warehouseMap.slotDetail.editItem")}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <IconX className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <Field label={t("warehouseMap.slotDetail.product")}>
            {mode === "add" ? (
              <Select
                placeholder={t("warehouseMap.slotDetail.selectProduct")}
                data={productData}
                value={productId || null}
                onChange={(value) => {
                  setProductId(value ?? "");
                  setError(null);
                }}
                searchable
                nothingFoundMessage={t("warehouseMap.slotDetail.productNotFound")}
                styles={selectStyles}
                classNames={selectClassNames}
              />
            ) : (
              <ReadOnlyValue>
                {selectedProduct?.name ?? item?.name ?? "—"}
              </ReadOnlyValue>
            )}
          </Field>

          <Field label={t("warehouseMap.slotDetail.quantity")}>
            <Input
              type="number"
              placeholder={t("warehouseMap.slotDetail.quantity")}
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setError(null);
              }}
            />
          </Field>

          <Field label={t("warehouseMap.slotDetail.limit")}>
            <Input
              type="number"
              placeholder={t("warehouseMap.slotDetail.limit")}
              value={limit}
              onChange={(e) => {
                setLimit(e.target.value);
                setError(null);
              }}
            />
          </Field>

          {/* {mode === "edit" && (
            <p className="text-xs text-zinc-500">上限僅能於新增商品時設定</p>
          )} */}

          {error && (
            <p className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-2 border-t border-[#30333c] px-5 py-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t("warehouseMap.cancel")}
          </Button>
          <Button className="flex-1" onClick={handleSubmit}>
            {mode === "add"
              ? t("warehouseMap.slotDetail.confirmAdd")
              : t("warehouseMap.slotDetail.saveChanges")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  item,
  onClose,
  onConfirm,
}: {
  item: InventoryItem;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl border border-[#30333c] bg-[#1a1d24] shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[#30333c] px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/10">
            <IconTrash className="h-5 w-5 text-red-400" />
          </div>
          <h3 className="text-base font-semibold text-zinc-100">{t("warehouseMap.slotDetail.deleteItem")}</h3>
        </div>

        <div className="px-5 py-5 text-sm text-zinc-300">
          <Trans
            i18nKey="warehouseMap.slotDetail.confirmDeleteItem"
            values={{ name: item.name }}
            components={{ b: <span className="font-medium text-zinc-100" /> }}
          />
        </div>

        <div className="flex gap-2 border-t border-[#30333c] px-5 py-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t("warehouseMap.cancel")}
          </Button>
          <Button
            className="flex-1 !bg-red-500 !text-white hover:!bg-red-400"
            onClick={onConfirm}
          >
            {t("warehouseMap.slotDetail.confirmDelete")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SlotDetailPanel({
  slot,
  products,
  onUpdateItem,
  onAddItem,
  onDeleteItem,
  onRename,
  onClose,
}: SlotDetailPanelProps) {
  const { t } = useTranslation();
  const [modal, setModal] = useState<
    { mode: "add" } | { mode: "edit"; item: InventoryItem } | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<InventoryItem | null>(null);
  const [renaming, setRenaming] = useState(false);

  if (!slot) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-zinc-400">
        <IconPackage className="mb-4 h-12 w-12 opacity-50" />
        <p className="text-center">{t("warehouseMap.slotDetail.emptyState")}</p>
      </div>
    );
  }

  const persisted = !slot.id.startsWith("slot-");

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#30333c] px-4 py-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setRenaming(true)}
            title={t("warehouseMap.slotDetail.clickToRenameSlot")}
            className="group flex max-w-full items-center gap-1.5 text-lg font-semibold text-zinc-100"
          >
            <span className="truncate">
              {t("warehouseMap.slotDetail.slotTitle", { name: slot.name })}
            </span>
            <IconPencil className="h-3.5 w-3.5 flex-shrink-0 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:opacity-100" />
          </button>
          <p className="text-sm text-zinc-400">{t("warehouseMap.slotDetail.productCount", { count: slot.items.length })}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <IconX className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {!persisted && (
          <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            {t("warehouseMap.slotDetail.saveLayoutFirst")}
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-zinc-400">{t("warehouseMap.slotDetail.productName")}</TableHead>
              <TableHead className="text-right text-zinc-400">{t("warehouseMap.slotDetail.quantity")}</TableHead>
              <TableHead className="text-right text-zinc-400">{t("warehouseMap.slotDetail.limit")}</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slot.items.map((item) => (
              <TableRow
                key={item.id}
                className="cursor-pointer hover:bg-[#2a2d35]/50"
                onClick={() => setModal({ mode: "edit", item })}
              >
                <TableCell className="font-medium text-zinc-100">
                  {item.name}
                </TableCell>
                <TableCell
                  className={`text-right ${
                    item.quantity > item.limit ? "text-red-400" : "text-zinc-100"
                  }`}
                >
                  {item.quantity}
                </TableCell>
                <TableCell className="text-right text-zinc-400">
                  {item.limit}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-teal-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal({ mode: "edit", item });
                      }}
                    >
                      <IconPencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(item);
                      }}
                    >
                      <IconTrash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Footer */}
      <div className="border-t border-[#30333c] p-4">
        <Button
          variant="outline"
          className="w-full"
          disabled={!persisted}
          onClick={() => setModal({ mode: "add" })}
        >
          <IconPlus className="mr-2 h-4 w-4" />
          {t("warehouseMap.slotDetail.addItem")}
        </Button>
      </div>

      {modal && (
        <ItemFormModal
          mode={modal.mode}
          products={products}
          existingItems={slot.items}
          item={modal.mode === "edit" ? modal.item : null}
          onClose={() => setModal(null)}
          onSubmitAdd={(draft) => {
            onAddItem(slot.id, draft);
            setModal(null);
          }}
          onSubmitEdit={(itemId, updates) => {
            onUpdateItem(slot.id, itemId, updates);
            setModal(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          item={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            onDeleteItem(slot.id, confirmDelete.id);
            setConfirmDelete(null);
          }}
        />
      )}

      {renaming && (
        <RenameModal
          title={t("warehouseMap.slotDetail.renameSlotTitle")}
          label={t("warehouseMap.slotDetail.slotNameLabel")}
          initialValue={slot.name}
          onClose={() => setRenaming(false)}
          onSubmit={(name) => {
            if (name !== slot.name) onRename(slot.id, name);
            setRenaming(false);
          }}
        />
      )}
    </div>
  );
}
